import { useRef, useState } from 'react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import type { RecordType } from '../types';
import type { NewRecordPayload } from '../hooks/useMedicalRecords';
import { RECORD_TYPES } from '../utils/constants';
import { UpgradeRequiredError } from '../utils/upgradeErrors';

interface DocumentUploadProps {
  petId: string;
  onSubmit: (payload: NewRecordPayload) => Promise<void>;
  onClose: () => void;
}

const RECORD_LABEL: Record<RecordType, string> = {
  vaccine:      'Vaccination',
  prescription: 'Prescription',
  lab:          'Lab Report',
};

const RECORD_EMOJI: Record<RecordType, string> = {
  vaccine:      '💉',
  prescription: '💊',
  lab:          '🔬',
};

/**
 * DocumentUpload — capture or pick an image of a veterinary document,
 * then attach a record type + date before uploading.
 *
 * On native (iOS / Android): uses @capacitor/camera to present the native
 * camera / photo library picker.
 * On web (dev / Cloudflare Pages): falls back to an HTML <input type="file">
 * so the flow still works without Capacitor.
 */
function DocumentUpload({ petId, onSubmit, onClose }: DocumentUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [recordType, setRecordType] = useState<RecordType>('prescription');
  const [recordDate, setRecordDate] = useState(new Date().toISOString().split('T')[0]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // ── Image capture ──────────────────────────────────────────────────────────

  const handleCapture = async () => {
    setErrorMsg('');

    // On native platforms use Capacitor Camera; on web fall back to file picker
    if (Capacitor.isNativePlatform()) {
      try {
        const photo = await Camera.getPhoto({
          quality: 85,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Prompt,  // lets user choose Camera or Gallery
        });

        if (photo.dataUrl) {
          setPreview(photo.dataUrl);
          // Convert data URL → File for the storage upload
          const res = await fetch(photo.dataUrl);
          const blob = await res.blob();
          setCapturedFile(
            new File([blob], `doc-${Date.now()}.${photo.format}`, { type: blob.type })
          );
        }
      } catch (err) {
        // User cancelled — not a real error
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.toLowerCase().includes('cancel')) {
          setErrorMsg('Could not access camera. Please check app permissions.');
        }
      }
    } else {
      // Web fallback: trigger hidden <input>
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // ── Form submit ────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    if (!capturedFile) { setErrorMsg('Please capture or select an image first.'); return; }
    if (!recordDate)   { setErrorMsg('Record date is required.'); return; }

    setIsUploading(true);
    try {
      await onSubmit({
        pet_id: petId,
        record_type: recordType,
        record_date: recordDate,
        file: capturedFile,
      });
      // Parent will close this component on success
    } catch (err) {
      if (UpgradeRequiredError.is(err)) {
        setIsUploading(false);
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Hidden web file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
        className="sr-only"
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <div className="w-full sm:max-w-md bg-brand-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 animate-slide-up max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 id="upload-title" className="font-display text-xl font-bold text-white">
            Upload Document
          </h2>
          <button id="close-upload" onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all">
            ✕
          </button>
        </div>

        <form id="upload-form" onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">

          {/* Image capture area */}
          <div>
            {preview ? (
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={preview}
                  alt="Document preview"
                  className="w-full h-48 object-cover"
                />
                <button
                  type="button"
                  id="retake-photo"
                  onClick={() => { setPreview(null); setCapturedFile(null); }}
                  className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-xl backdrop-blur-sm hover:bg-black/80 transition-all"
                >
                  Retake
                </button>
              </div>
            ) : (
              <button
                type="button"
                id="capture-photo"
                onClick={() => void handleCapture()}
                className="w-full h-36 rounded-2xl border-2 border-dashed border-brand-500/40 bg-brand-500/5
                           flex flex-col items-center justify-center gap-2
                           hover:border-brand-400/60 hover:bg-brand-500/10 transition-all"
              >
                <span className="text-3xl">📷</span>
                <span className="text-sm font-medium text-brand-300">
                  {Capacitor.isNativePlatform() ? 'Take Photo / Choose from Gallery' : 'Select Image or PDF'}
                </span>
                <span className="text-xs text-white/30">JPEG · PNG · HEIC · PDF · Max 10 MB</span>
              </button>
            )}
          </div>

          {/* Record type */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Record Type</label>
            <div className="grid grid-cols-3 gap-2">
              {RECORD_TYPES.map((rt) => (
                <button
                  key={rt}
                  type="button"
                  id={`record-type-${rt}`}
                  onClick={() => setRecordType(rt)}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-medium transition-all duration-200 ${
                    recordType === rt
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <span className="text-lg">{RECORD_EMOJI[rt]}</span>
                  {RECORD_LABEL[rt]}
                </button>
              ))}
            </div>
          </div>

          {/* Record date */}
          <div>
            <label htmlFor="record-date" className="block text-sm font-medium text-white/80 mb-1">
              Document Date <span className="text-red-400">*</span>
            </label>
            <input
              id="record-date"
              type="date"
              required
              value={recordDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setRecordDate(e.target.value)}
              className="input-field [color-scheme:dark]"
            />
          </div>

          {/* Error */}
          {errorMsg && (
            <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" id="cancel-upload" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              id="submit-upload"
              disabled={isUploading || !capturedFile}
              className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  Uploading…
                </>
              ) : (
                '⬆ Upload'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DocumentUpload;
